"use client"

import {
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react"
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react"
import {
  APPLICATION_FORM_VERSION,
  answersCarriedIntoUnifiedForm,
  applicationStepsForAnswers,
  isMoneyArm,
  recommendedProductFor,
  resumeStepIndex,
  type ApplicationStep,
  type ApplyProduct,
  type ApplyProductCard,
  type MentorshipFocus,
  sanitizeAnswers,
  type ApplicationAnswers,
  type ApplicationQuestion,
} from "@/lib/coaching/application"
import { type ProgramTag } from "@/lib/booking"
import { APPLICATION_CAL_LINK, APPLICATION_CALL_SLUG } from "@/lib/booking/call-types"
import { HoneypotField } from "@/components/marketing/honeypot-field"
import { ApplyBookingPicker } from "@/components/marketing/apply-booking-picker"
import { marketingActionClass } from "@/components/marketing/marketing-action"
import {
  AnalyticsEvent,
  applyEventProps,
  firstTouchSummary,
  getPosthogDistinctId,
  situationQueryFromUrl,
  stitchLeadEmail,
  track,
} from "@/lib/analytics"
import {
  entryCtaFromUrl,
  entryPageFromReferrer,
  readAdClickId,
} from "@/lib/leads/entry-attribution"
import { readAcquisitionContext } from "@/lib/acquisition/context"
import { HONEYPOT_FIELD } from "@/lib/honeypot"
import { cn } from "@/lib/utils"
import {
  clearMetaLeadSession,
  DRAFT_META_ID_KEY,
  DRAFT_STEP_ID_KEY,
  DRAFT_STEP_INDEX_KEY,
  DRAFT_STORAGE_KEY,
  eligibleDraftId,
  eligibleLeadId,
  eligibleMetaBindingToken,
  eligibleMetaEventId,
  historyMetaTabClaim,
  isReloadNavigation,
  LEAD_ID_SESSION_KEY,
  LEAD_META_EVENT_SESSION_KEY,
  META_BINDING_SESSION_KEY,
  mintDraftId,
  readSessionValue,
  readStoredDraftId,
  writeHistoryMetaTabClaim,
  writeSessionValue,
} from "./application-flow-meta-binding"
import { BOOKING_STEP_ID, CONTACT_STEP_ID, createApplyMilestones } from "./application-flow-milestones"
import { ProductCardLink, QuestionInput } from "./application-flow-fields"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Hydration detection via useSyncExternalStore: the store never changes, so
// subscribe is a no-op returning a no-op unsubscribe.
const subscribeNoop = () => () => {}

/** The product steps whose small link carries on toward the call. The "Not
 * yet" step has none: it ends on the product. */
const PRODUCT_STEP_IDS_WITH_CONTINUE = new Set(["dfy_product", "money_product"])

/** The step ids in order, the key the step list is held stable on. */
function stepIdsKey(steps: ApplicationStep[]): string {
  return steps.map((step) => step.id).join("|")
}

/** Keeps the first `value` seen for each `key`: a later render with the same
 * key gets that same object back, a new key adopts the new value. This is
 * useMemo(() => value, [key]) without leaving `value` out of the dependency
 * list, and unlike useMemo, React never discards it. A new key stores the new
 * value during render, which makes React render the component again at once. */
function useStableByKey<T>(value: T, key: string): T {
  const [held, setHeld] = useState({ key, value })
  if (held.key !== key) {
    setHeld({ key, value })
    return value
  }
  return held.value
}

type ApplyValidationFailure = {
  field_id: string
  error_type: "required" | "invalid_email"
  message: string
}

type ApplyJsonPayload = { [key: string]: unknown }

/** The booking the picker confirmed, as /api/apply/book returned it. */
type ConfirmedBooking = { uid?: string; start?: string; end?: string; submitProof?: string }

export function ApplicationFlow({
  programTag,
  focusTag,
  entrySource,
  bookingUrl,
  resumeToken,
  resumeRef,
  productCards,
  children,
}: {
  /** The product each product step can show, built on the server from the
   * product's own checkout copy, live price and page
   * (web/lib/apply-product-cards.ts). */
  productCards: Record<ApplyProduct, ApplyProductCard>
  /** Rendered inside the vertically centred column, under the form. The trust
   * badges and the applied-this-week line belong to the form's visual unit, and
   * the marketing layout stretches `main` to the viewport, so anything left
   * OUTSIDE this centred block gets stranded above the footer by whatever slack
   * is left over — 348px of it on the first step, which is what the owner saw. */
  children?: ReactNode
  /** The `program` to stamp on the lead — from an explicit offer's Apply
   * button (?offer=) or the server-side entry-touch cookie fallback
   * (lib/leads/first-touch.ts). Undefined for a bare /apply visit with
   * neither signal, so the lead is left untagged rather than defaulting to
   * "bootcamp". */
  programTag?: ProgramTag
  /** A vertical Mentorship page's acquisition context (/apply?focus=…). Seeds
   * `answers.mentorship_focus`, which swaps the routing + meeting-women steps
   * for that focus's one diagnostic step. Undefined everywhere else, so the
   * ordinary application is untouched. */
  focusTag?: MentorshipFocus
  /** A short, generic, no-PII "how they got here" label from the entry-touch
   * cookie (lib/leads/first-touch.ts) — used ONLY as a fallback for
   * `answers.source` when the client's own localStorage-based
   * `firstTouchSummary()` comes back empty (private browsing, cleared
   * storage). Never overrides a real client-captured summary. */
  entrySource?: string
  /** Booking-page URL (Cal.com / Google Appointment Scheduling). When set, the
   * end of the application embeds it so the applicant books their call right
   * away; when empty, we fall back to the "we'll email you" confirmation. */
  bookingUrl?: string
  /** Signed resume token from `/apply?resume=…` for cross-device draft hydration. */
  resumeToken?: string
  /** The purpose-scoped `resume_application` reference from `/apply?ref=…`,
   * minted beside the token (web/lib/prospects/signed-reference.ts). It rides
   * on the resume fetch as `&ref=`, where the route verifies it for exactly
   * that purpose and opens the draft by row id; the token stays beside it as
   * the fallback until 1F. Read only with `resumeToken`, because the token is
   * what makes a visit an explicit resume. */
  resumeRef?: string
}) {
  // Only seed `program` when we have an explicit offer tag; a bare visit leaves
  // the key absent so the stored lead isn't mislabeled with a default program.
  // The focus stamp rides the same seed: present only when a vertical page
  // sent the visit, absent otherwise. The version stamp is always present:
  // this is the unified form, and every answer set it saves says so.
  const programSeed: ApplicationAnswers = {
    form_version: APPLICATION_FORM_VERSION,
    ...(programTag ? { program: programTag } : {}),
    ...(focusTag ? { mentorship_focus: focusTag } : {}),
  }
  // The local draft, read ONCE: his answers and the step he was on. A visit
  // with an explicit resume token reads none of it, because that token owns the
  // visit and its server answers are still hydrating; an unrelated local draft
  // must never be rendered or POSTed then.
  //
  // The position is what the last server save carried (writeDraftNow keeps it
  // beside the answers). It resolves exactly as a ?resume= visit does: by step
  // id first, then by index, then capped at the first step with a missing or
  // invalid answer (resumeStepIndex). A draft written before the position was
  // kept has none and starts at step 1, as it always did.
  const [restored] = useState<{
    answers: ApplicationAnswers
    index: number
    position: { id: string; index: number } | null
  } | null>(() => {
    if (typeof window === "undefined" || resumeToken) return null
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY)
      if (!saved) return null
      const parsed = JSON.parse(saved) as Record<string, unknown>
      const savedId = parsed[DRAFT_STEP_ID_KEY]
      const rawIndex = parsed[DRAFT_STEP_INDEX_KEY]
      const savedIndex =
        typeof rawIndex === "number"
          ? rawIndex
          : typeof rawIndex === "string" && rawIndex.trim() !== ""
            ? Number(rawIndex)
            : Number.NaN
      delete parsed[DRAFT_STEP_ID_KEY]
      delete parsed[DRAFT_STEP_INDEX_KEY]
      // Drop answers that no longer resolve to a valid option (a stale draft
      // from before a form change) so the applicant never dead-ends at Submit.
      // A draft an earlier version saved is carried into the unified form
      // first, which keeps only the answers to questions the unified form
      // asks word for word: an old answer is never re-read as the answer to
      // a question he was not asked.
      const restoredAnswers = sanitizeAnswers({
        ...answersCarriedIntoUnifiedForm(parsed as ApplicationAnswers),
        ...programSeed,
      })
      const ids = applicationStepsForAnswers(restoredAnswers).map((candidate) => candidate.id)
      const byId = typeof savedId === "string" ? ids.indexOf(savedId) : -1
      const position =
        byId >= 0
          ? { id: ids[byId], index: byId }
          : Number.isFinite(savedIndex) && savedIndex >= 0 && savedIndex < ids.length
            ? { id: ids[Math.floor(savedIndex)], index: Math.floor(savedIndex) }
            : null
      return {
        answers: restoredAnswers,
        index: resumeStepIndex(restoredAnswers, position?.index),
        position,
      }
    } catch {
      // Ignore parsing errors; fall back to fresh state
      return null
    }
  })
  const [answers, setAnswers] = useState<ApplicationAnswers>(() => restored?.answers ?? { ...programSeed })
  const [stepIndex, setStepIndex] = useState(restored?.index ?? 0)
  const [error, setError] = useState<string | null>(null)
  // The Prospect Pipeline milestones (lead_captured, application_started,
  // calendar_reached), one emitter per mount so each fires once per visit and
  // application_started can never overtake lead_captured. See
  // application-flow-milestones.ts for why the ordering needs a state machine.
  const [milestones] = useState(() => createApplyMilestones())
  // The exact URL the redirecting interstitial's fallback link opens. State,
  // not a ref, because render reads it. Set at the moment we call
  // window.location.assign, so the link can never point somewhere the flow
  // does not actually go: it used to point at NEXT_PUBLIC_APPLY_BOOKING_URL,
  // an env var that may be unset (href undefined) and that names a page this
  // flow stopped navigating to when booking moved before submit.
  const [redirectTarget, setRedirectTarget] = useState("/what-happens-next")
  const [status, setStatus] = useState<
    "idle" | "submitting" | "redirecting" | "done" | "error"
  >("idle")
  const honeypotContainerRef = useRef<HTMLDivElement>(null)

  function withApplyHoneypot(payload: ApplyJsonPayload): ApplyJsonPayload {
    const value =
      honeypotContainerRef.current
        ?.querySelector<HTMLInputElement>(`input[name="${HONEYPOT_FIELD}"]`)
        ?.value ?? ""
    return value.trim() ? { ...payload, [HONEYPOT_FIELD]: value } : payload
  }
  // Post-mount flag. `answers` is seeded from localStorage in a lazy initializer,
  // so the server renders the empty-state value and the client renders the
  // restored value. Any render attribute derived from `answers` (the disabled
  // Continue button below) must wait for this flag or it hydrate-mismatches and
  // React can leave the attribute stuck at the server value — a returning
  // visitor's Continue once stayed disabled with their name prefilled. See
  // web/AGENTS.md (lazy-initializer hydration footgun).
  // useSyncExternalStore returns false on the server + first client render, then
  // true after hydration - the hydration-safe way to gate a render attribute
  // WITHOUT calling setState inside an effect (which the react-hooks lint rejects
  // as a cascading render). Replaces the old useState + useEffect(setHydrated).
  const hydrated = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  )
  const [draftId] = useState(() =>
    typeof window === "undefined"
      ? null
      : resumeToken
        ? mintDraftId()
        : readStoredDraftId() ?? mintDraftId(),
  )
  const draftIdRef = useRef(draftId)
  // The step the last server save carried, kept beside the answers in the
  // local draft so a return visit resumes there. Set where a save is queued
  // (saveDraft) and where a resume link places him, never from a render.
  const draftPositionRef = useRef<{ id: string; index: number } | null>(restored?.position ?? null)
  const [storedLeadIdentity] = useState(() => {
    if (typeof window === "undefined" || resumeToken) {
      return { leadId: null, metaBindingToken: null, metaEventId: undefined }
    }
    const storedDraftId = readStoredDraftId()
    const leadId = eligibleLeadId(readSessionValue(LEAD_ID_SESSION_KEY)) ?? null
    const metaBindingToken =
      eligibleMetaBindingToken(readSessionValue(META_BINDING_SESSION_KEY)) ?? null
    if (
      !draftId ||
      storedDraftId !== draftId ||
      historyMetaTabClaim() !== draftId ||
      !isReloadNavigation() ||
      !leadId ||
      !metaBindingToken
    ) {
      return { leadId: null, metaBindingToken: null, metaEventId: undefined }
    }
    return {
      leadId,
      metaBindingToken,
      metaEventId: eligibleMetaEventId(readSessionValue(LEAD_META_EVENT_SESSION_KEY)),
    }
  })
  // A UUID labels the occurrence but never authorizes it. The paired signed,
  // expiring capability is scoped to Meta binding and lives only in this tab.
  // Explicit resume links use their own server credential and inherit neither.
  const leadIdRef = useRef(resumeToken ? null : storedLeadIdentity.leadId)
  const metaBindingTokenRef = useRef(
    resumeToken ? null : storedLeadIdentity.metaBindingToken,
  )
  // True engagement: first keystroke or option tap, once per visit.
  const startedSent = useRef(false)
  // The browser Lead one-shot is marked only after the API returns a validated
  // 64-hex id for the durable transition.
  const leadSent = useRef(
    !resumeToken &&
      Boolean(storedLeadIdentity.metaBindingToken && storedLeadIdentity.metaEventId),
  )
  // An explicit resume defaults to suppressing the browser copy until its
  // successful hydration proves this visit began before the durable occurrence.
  // The result is immutable for the visit: a lost first-save response followed
  // by a retry must not reclassify the newly stamped occurrence as pre-existing.
  const resumedLead = useRef(Boolean(resumeToken))
  const resumeLeadDecisionSealed = useRef(false)
  // TRUE when he arrived on a resume link and the hydration never delivered his
  // stored answers. The form still unlocks, so without telling the server this,
  // his next save looks like a man who deliberately cleared every answer.
  const resumeUnhydrated = useRef(false)
  // A resume must finish hydrating before a local draft can autosave. State is
  // used here because changing a ref alone would not restart the autosave effect.
  const [resumeReady, setResumeReady] = useState(!resumeToken)
  // Exit-intent: fired at most once after apply_started when a mid-form visitor
  // looks like they're leaving. Analytics only — the abandon-reason bottom
  // sheet that once rode this trigger is removed (owner: it was annoying).
  const exitIntentSent = useRef(false)
  // Mirrors of live state for the once-bound exit-intent listeners.
  const statusRef = useRef<typeof status>("idle")
  const stepRef = useRef(0)
  const stepIdRef = useRef("")
  const questionsRef = useRef<HTMLDivElement>(null)
  const stepColumnRef = useRef<HTMLDivElement>(null)
  // The confirmed booking from the final step. Null until Cal fires
  // bookingSuccessful, and Submit stays locked the whole time it is null.
  // `submitProof` is the server's booked-submit proof for it, sent back with
  // the submit and never put in a URL (web/lib/leads/booked-submit.ts).
  const [booking, setBooking] = useState<ConfirmedBooking | null>(null)
  // The heights the booking step occupied at the moment the booking confirmed,
  // held as min-heights so nothing on the page moves when the open picker
  // turns into its one "Booked:" line and then the redirect spinner replaces
  // the form. Without them that collapse, which lands after the booking
  // round-trip and so is no longer excused as a response to the tap, measured
  // 0.40 layout shift on a 390px phone, and the field reading from a phone
  // that booked was 0.38. Null until a booking confirms, and null again once
  // the application submit after it fails: from then on the page is no longer
  // handing off, and the held picker height would leave a blank band of
  // several hundred pixels under the one-line "Booked:" text, pushing the
  // error and the Finish retry toward the bottom of the screen.
  const [heldHeights, setHeldHeights] = useState<{ picker: number; root: number } | null>(null)
  const pickerBoxRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // The purpose-scoped booking_handoff reference from the most recent lead
  // save, bound to the row id rather than to the address
  // (web/lib/prospects/signed-reference.ts). Handed to the picker so
  // /api/apply/book can forward it into Cal metadata once it verifies, and the
  // webhook then attributes the booking to this exact attempt even when the
  // applicant types a different address into Cal's own form. It replaced an
  // email-derived signature that carried the address itself.
  const [prospectRef, setProspectRef] = useState<string | null>(null)
  // A booking IS the submission (owner, 2026-07-25): if he books and then closes
  // the tab without pressing the button, we still want the application. This
  // guards against sending it twice when he does press it.
  const submittedRef = useRef(false)
  // Set only after the server confirms the application landed, so a failed POST
  // can still be retried by pressing the button.
  const alreadyFiledRef = useRef(false)
  // Seals the Lead-save queue while Submit awaits its current tail. Without this,
  // a debounce firing one tick later could append work after the awaited snapshot.
  const submissionStartedRef = useRef(false)
  const hasMountedRef = useRef(false)
  // Timer for debounced localStorage saves
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null)
  // Lead saves are serialized so an email correction cannot start row B before
  // row A's durable occurrence id has returned and become the request binding.
  const leadSaveQueueRef = useRef<Promise<void>>(Promise.resolve())
  // Submission advances the generation before clearing browser identity. Any
  // response from the prior generation becomes inert even if it was in flight.
  const leadSaveGenerationRef = useRef(0)
  // Delays the auto-advance on a single-question step by one paint so the tapped
  // option's "selected" highlight actually renders before the step transitions.
  // Advancing synchronously batches the answer update and the step change into a
  // single React render, so the tap never visibly registers and PostHog logs it
  // as a dead click (the /apply dead-click fix, 2026-07-14).
  const advanceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // ONE question set for everyone: the form no longer branches, and the event
  // property that used to name the branch names the unified form instead.
  const branch = "unified"
  // The money experiment has ended: every man sees the price range, and the
  // form never assigns an arm. A row saved during the experiment keeps the arm
  // it was shown, and it is still reported on its events, but nothing derives
  // a new one.
  const moneyArm = isMoneyArm(answers.money_arm) ? answers.money_arm : null
  // HELD STABLE ON THE STEP IDS, and it is load-bearing rather than tidy. The
  // unified steps depend on his answers, so they are recomputed every render,
  // and every keystroke re-renders through setAnswer. `steps` sits in the
  // step-viewed effect's dependency array, so a fresh array per keystroke
  // fired apply_step_viewed once per character typed, which inflates the top
  // of the apply funnel by however much a man types. Every step is a module
  // constant, so two lists with the same ids are the same list, and the held
  // list only moves when an answer actually changes which steps he will see.
  // The id sequence is the whole key: a list whose ids stay the same while a
  // step's content changes (the money step's three variants all share id
  // "money") would keep rendering the old content.
  const computedSteps = applicationStepsForAnswers(answers)
  const steps = useStableByKey(computedSteps, stepIdsKey(computedSteps))
  const total = steps.length
  // A resume can switch branches after mount, and the two branches are
  // different lengths, so never index past the end of the list in play.
  //
  // The server render and the hydrating render show step 1, exactly as the
  // server HTML; the restored step appears right after hydration. The focus
  // and scroll effect is keyed on `stepIndex` itself, so it does not fire on
  // load.
  const shownStepIndex = hydrated ? stepIndex : 0
  const safeIndex = Math.min(shownStepIndex, total - 1)
  const step = steps[safeIndex]
  const isLast = safeIndex === total - 1
  // The product a product step shows, and its card. Null on every other step.
  const recommendedProduct = recommendedProductFor(step.id, answers)
  const productCard = recommendedProduct ? productCards[recommendedProduct] : null
  // The server render has no local draft, so until hydration the rail is drawn
  // against the step list the server drew. Restored answers can add a step (a
  // product screen), and a rail drawn against that longer list is a hydration
  // mismatch React never patches.
  const railTotal = hydrated ? total : applicationStepsForAnswers(programSeed).length
  const progress =
    status === "done" ? 100 : Math.round(((safeIndex + 1) / railTotal) * 100)
  // Gate Continue the moment a required question on the step is unanswered (or an
  // email is malformed): the button reads as visibly disabled instead of looking
  // fully active and silently no-op'ing on tap — the "dead click" the owner
  // flagged. Reflected into render attributes only after mount (see `hydrated`).
  const stepFailure = getStepValidation(answers)
  const stepBlocked = stepFailure !== null
  const continueDisabled = !resumeReady || status === "submitting" || (hydrated && stepBlocked)
  const fieldErrorId =
    stepFailure &&
    step.questions.some((question) => question.id === stepFailure.field_id) &&
    (error === stepFailure.message || stepFailure.error_type === "invalid_email")
      ? `q-${stepFailure.field_id}-error`
      : null

  // Move focus to the new question when the step changes (not on first mount),
  // so keyboard + screen-reader users land on the new content. The focus and
  // the scroll are SEPARATE on purpose: a bare .focus() scrolls the element to
  // the exact top of the visual viewport, which on a phone parks the question
  // label underneath the 65px sticky masthead — worst mid-form, when the iOS
  // keyboard shrinks the visual viewport during the step transition and the
  // scroll offset survives the keyboard collapsing (the owner's "form looks
  // fucked up" screenshot). preventScroll plus an explicit scrollIntoView
  // against the scroll-mt on the step's column keeps the heading below the bar.
  //
  // The scroll happens ONLY when the new step is out of view: its column
  // (eyebrow first) above the masthead clearance, or its question below the
  // bottom of what is visible, keyboard included. A step that already sits in
  // view stays put. Scrolling unconditionally moved any step taller than the
  // screen, so the booking step on a 390x844 phone opened 50px down with the
  // progress rail gone and the "Book your call" eyebrow half under the
  // masthead. The column, not the questions div, is the target so that when a
  // scroll is needed the eyebrow lands below the bar too.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    const questions = questionsRef.current
    const column = stepColumnRef.current
    questions?.focus({ preventScroll: true })
    if (!questions || !column) return
    const view = window.visualViewport
    const viewTop = view ? view.offsetTop : 0
    const viewBottom = view ? view.offsetTop + view.height : window.innerHeight
    const clearance = parseFloat(getComputedStyle(column).scrollMarginTop) || 0
    const columnHidden = column.getBoundingClientRect().top < viewTop + clearance
    const questionHidden = questions.getBoundingClientRect().top > viewBottom - clearance
    if (columnHidden || questionHidden) column.scrollIntoView({ block: "start" })
  }, [stepIndex])

  // Clear any pending auto-advance timer on unmount. (Hydration gating moved to
  // the useSyncExternalStore above so no setState happens inside this effect.)
  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
    }
  }, [])

  // Warm the Cal booking embed so /book opens instantly (owner, 2026-07-21).
  // Booking now happens on this form's own last step through our picker, so
  // /book is reached only from that step's "Calendar not loading?" link. The
  // warm-up therefore starts when the applicant reaches the booking step, not
  // on landing: the embed and its iframe are megabytes of third-party script,
  // and loading them with the first question made /apply the slowest page on
  // the site. Lazy-imported, once per visit, best-effort: a failure here
  // never affects the form.
  const onBookingStep = step.id === BOOKING_STEP_ID
  const calPreloaded = useRef(false)
  useEffect(() => {
    if (!onBookingStep || calPreloaded.current) return
    calPreloaded.current = true
    void (async () => {
      try {
        const { getCalApi } = await import("@calcom/embed-react")
        const cal = await getCalApi({ namespace: APPLICATION_CALL_SLUG })
        cal("preload", { calLink: APPLICATION_CAL_LINK })
      } catch {
        /* preload is best-effort */
      }
    })()
  }, [onBookingStep])

  function stepEventProps(extra?: Record<string, string | number | boolean | null>) {
    return {
      ...applyEventProps(programTag ?? null, moneyArm),
      step_id: step.id,
      step_index: safeIndex,
      step_number: safeIndex + 1,
      branch,
      ...extra,
    }
  }

  function markStarted(inputType: "keystroke" | "selection") {
    if (startedSent.current) return
    startedSent.current = true
    track(AnalyticsEvent.ApplyStarted, {
      ...applyEventProps(programTag ?? null, moneyArm),
      input_type: inputType,
    })
  }

  function writeDraftNow(source: ApplicationAnswers): void {
    if (typeof window === "undefined") return
    try {
      // Don't persist the program field, since it's set from the program tag.
      const toPersist: Record<string, string> = { ...source }
      delete toPersist.program
      if (draftIdRef.current) {
        toPersist[DRAFT_META_ID_KEY] = draftIdRef.current
      }
      if (draftPositionRef.current) {
        toPersist[DRAFT_STEP_ID_KEY] = draftPositionRef.current.id
        toPersist[DRAFT_STEP_INDEX_KEY] = String(draftPositionRef.current.index)
      }
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(toPersist))
    } catch {
      // Silently ignore localStorage errors (quota, etc.)
    }
  }

  // Save answers to localStorage with debounce (avoid thrashing)
  const persistAnswers = (source: ApplicationAnswers) => {
    if (typeof window === "undefined") return
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }
    saveTimerRef.current = setTimeout(() => {
      writeDraftNow(source)
    }, 500)
  }

  // Funnel: fire when the visitor reaches a step (initial step on mount, plus
  // every forward/back move). Measures step reach, not engagement.
  //
  // NOT BEFORE HYDRATION. The answers are restored from the local draft in a
  // lazy initializer, so the hydrating render carries the server's empty
  // answers and the next render the restored ones; a restored answer can change
  // which steps he sees, and reporting both renders counted step one twice.
  // `hydrated` flips after hydration, so the step is reported once, against the
  // steps he actually sees. A client-side navigation mounts already hydrated
  // and is unchanged.
  useEffect(() => {
    if (!hydrated) return
    const current = steps[Math.min(stepIndex, steps.length - 1)]
    const reached = {
      ...applyEventProps(programTag ?? null, moneyArm),
      step_id: current.id,
      step_index: Math.min(stepIndex, steps.length - 1),
      step_number: Math.min(stepIndex, steps.length - 1) + 1,
      total: steps.length,
      branch,
    }
    track(AnalyticsEvent.ApplyStepViewed, reached)
    // The booking step rendering IS calendar_reached: every required question
    // answered and the inline calendar on screen. Progress, never a submission.
    milestones.stepReached(current.id, reached)
  }, [hydrated, stepIndex, programTag, steps, branch, milestones, moneyArm])

  // Cross-device resume: hydrate answers + step from server when ?resume= is set.
  useEffect(() => {
    if (resumeToken || !storedLeadIdentity.metaBindingToken) {
      clearMetaLeadSession()
    }
  }, [resumeToken, storedLeadIdentity.metaBindingToken])

  useEffect(() => {
    if (!resumeToken) return
    let cancelled = false
    void (async () => {
      // Every named way this fails is transient - a 500, the resume route's own
      // 30/hr/IP bucket, a dropped mobile fetch - so one retry converts most of
      // them into a normal hydration instead of into the destructive path below.
      const hydrate = async () => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 700))
          if (cancelled) return null
          try {
            // The token stays first; the reference rides beside it when the
            // link carried one, and the route dual-accepts either.
            const r = await fetch(
              `/api/coaching/apply/resume?token=${encodeURIComponent(resumeToken)}${resumeRef ? `&ref=${encodeURIComponent(resumeRef)}` : ""}`,
            )
            if (r.ok) return r
          } catch {
            // Next attempt, then give up and mark the visit unhydrated.
          }
        }
        return null
      }
      try {
        const res = await hydrate()
        if (!res) {
          resumeUnhydrated.current = true
          return
        }
        const data = (await res.json().catch(() => null)) as {
          answers?: ApplicationAnswers
          last_step_index?: number
          has_meta_lead_occurrence?: boolean
        } | null
        if (cancelled) return
        if (!data?.answers) resumeUnhydrated.current = true
        // A resumed row continues in the unified form whatever version saved
        // it. A row an earlier version saved keeps only the answers to
        // questions the unified form asks word for word (never an old answer
        // re-read as the answer to a new question), is sanitized as the
        // unified form, and picks up no later than the first step still
        // missing an answer: it holds a position in a different list.
        const merged = data?.answers
          ? sanitizeAnswers(
              answersCarriedIntoUnifiedForm({
                ...(programTag ? { program: programTag } : {}),
                ...(focusTag ? { mentorship_focus: focusTag } : {}),
                ...data.answers,
              }),
            )
          : null
        if (merged) {
          setAnswers(merged)
          persistAnswers(merged)
        }
        if (typeof data?.last_step_index === "number") {
          const resumedIndex = merged
            ? resumeStepIndex(merged, data.last_step_index)
            : Math.max(0, data.last_step_index)
          setStepIndex(resumedIndex)
          const resumedStep = merged ? applicationStepsForAnswers(merged)[resumedIndex] : undefined
          if (resumedStep) draftPositionRef.current = { id: resumedStep.id, index: resumedIndex }
        }
        // This durable DB signal, not mutable form progress, decides whether an
        // explicit/cross-device resume has already emitted its browser Lead.
        if (
          !resumeLeadDecisionSealed.current &&
          typeof data?.has_meta_lead_occurrence === "boolean"
        ) {
          resumedLead.current = data.has_meta_lead_occurrence
          resumeLeadDecisionSealed.current = true
        }
      } catch {
        // Best-effort — visitor can still fill the form fresh, but the save must
        // not then read as him clearing everything he had already answered.
        resumeUnhydrated.current = true
      } finally {
        if (!cancelled) {
          // A failed or malformed hydration keeps the fail-closed default. Once
          // interaction begins, no retry or prop change may reclassify the visit.
          resumeLeadDecisionSealed.current = true
          setResumeReady(true)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // persistAnswers intentionally closes over this visit's immutable draft id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeToken, resumeRef, programTag, focusTag])

  // Keep the listener mirrors current (refs so the once-bound handlers below
  // never read stale closure state). Runs after every render commit — refs
  // must not be written directly in the render body.
  useEffect(() => {
    statusRef.current = status
    stepRef.current = safeIndex
    stepIdRef.current = step.id
  })


  // Exit intent, once per visit: only after apply_started.
  useEffect(() => {
    const fire = () => {
      if (
        exitIntentSent.current ||
        statusRef.current !== "idle" ||
        !startedSent.current
      ) {
        return
      }
      exitIntentSent.current = true
      track(AnalyticsEvent.ApplyExitIntent, {
        ...applyEventProps(programTag ?? null, moneyArm),
        step_number: stepRef.current + 1,
      })
    }
    const onMouseOut = (event: MouseEvent) => {
      if (!event.relatedTarget && event.clientY <= 0) fire()
    }
    const onVisibility = () => {
      if (document.visibilityState === "hidden") fire()
    }
    document.addEventListener("mouseout", onMouseOut)
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      document.removeEventListener("mouseout", onMouseOut)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [programTag, moneyArm])

  function getStepValidation(source: ApplicationAnswers): ApplyValidationFailure | null {
    // The booking step carries no questions — the calendar IS the answer.
    // No confirmed booking, no Submit.
    if (step.id === BOOKING_STEP_ID) {
      return booking
        ? null
        : {
            field_id: "booking",
            error_type: "required",
            message: "Pick a time above to finish your application.",
          }
    }
    for (const q of step.questions) {
      const value = (source[q.id] ?? "").trim()
      if (q.required && !value) {
        return {
          field_id: q.id,
          error_type: "required",
          message: "Answer this one to keep going.",
        }
      }
      if (value && q.type === "email" && !EMAIL_RE.test(value)) {
        return {
          field_id: q.id,
          error_type: "invalid_email",
          message: "That email address doesn't look right.",
        }
      }
    }
    return null
  }

  // The client's own localStorage-based first-touch summary, falling back to
  // the server-derived entry-touch source (lib/leads/first-touch.ts) only
  // when localStorage never captured anything real (private browsing,
  // cleared storage) — firstTouchSummary() returns the literal string
  // "direct / unknown" in that case. Never overrides a real captured summary.
  function resolveSource(): string {
    const local = firstTouchSummary()
    if (local && local !== "direct / unknown") return local
    return entrySource ?? local
  }

  /**
   * The page the Apply button was on, which button it was, the ad click id, and
   * the optional `?situation=` a situation page's CTA carried. Read at save
   * time rather than on mount: the referrer is stable for the life of the
   * page, and reading it late keeps it out of the hydration path. The server
   * derives the situation itself from `entry_page`; `situation` only lets a
   * CTA be explicit, and the server validates it against the registry.
   */
  function resolveEntry() {
    return {
      money_arm: moneyArm ?? undefined,
      entry_page: entryPageFromReferrer(),
      entry_cta: entryCtaFromUrl(),
      ad_click_id: readAdClickId(),
      situation: situationQueryFromUrl(),
      // This visit's acquisition envelope, minted on the first page it saw
      // (instrumentation-client.ts) and read here for the same reason as the
      // referrer above: late, so it stays out of the hydration path. Absent
      // when storage is unavailable, which is exactly what a save before this
      // existed carried. See web/lib/acquisition/context.ts.
      acquisition_context: readAcquisitionContext() ?? undefined,
    }
  }

  function trackValidationError(failure: ApplyValidationFailure) {
    track(AnalyticsEvent.ApplyValidationError, {
      ...stepEventProps(),
      field_id: failure.field_id,
      error_type: failure.error_type,
    })
  }

  function setAnswer(id: string, value: string) {
    markStarted("keystroke")
    setAnswers((prev) => {
      const updated = { ...prev, [id]: value }
      persistAnswers(updated)
      return updated
    })
    setError(null)
  }

  async function submit(
    source: ApplicationAnswers,
    confirmed?: ConfirmedBooking,
  ) {
    if (!resumeReady) return
    // A booking files the application immediately, so by the time the button is
    // pressed the work is usually already done. Re-POSTing would be a duplicate
    // team notification for one applicant; go straight to the confirmation.
    if (alreadyFiledRef.current) {
      const done = confirmed ?? booking
      if (done && typeof window !== "undefined") {
        const params = new URLSearchParams()
        if (done.uid) params.set("uid", done.uid)
        if (done.start) params.set("start", done.start)
        if (done.end) params.set("end", done.end)
        const query = params.toString()
        const target = `/what-happens-next${query ? `?${query}` : ""}`
        setRedirectTarget(target)
        setStatus("redirecting")
        window.location.assign(target)
      }
      return
    }
    if (submissionStartedRef.current) return
    submissionStartedRef.current = true
    setStatus("submitting")
    setError(null)
    try {
      // advanceFrom queues the final durable Lead save immediately before it
      // calls submit. Waiting here guarantees the application POST carries the
      // canonical Lead binding learned from that response.
      await leadSaveQueueRef.current
      if (alreadyFiledRef.current) return
      // The proof that this submit follows a real booking, so the route never
      // refuses it at its per-IP submit cap. Server-minted by /api/apply/book
      // and server-verified against the email below; absent before a booking.
      const bookedSubmitProof = (confirmed ?? booking)?.submitProof
      const res = await fetch("/api/coaching/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          withApplyHoneypot({
            answers: { ...source, source: resolveSource(), ...(moneyArm ? { money_arm: moneyArm } : {}) },
            posthog_distinct_id: getPosthogDistinctId(),
            ...resolveEntry(),
            ...(resumeUnhydrated.current ? { unhydrated_resume: true } : {}),
            lead_id: leadIdRef.current,
            resume_token: resumeToken,
            meta_binding_token: metaBindingTokenRef.current,
            meta_draft_id: draftIdRef.current,
            ...(bookedSubmitProof ? { booked_submit_proof: bookedSubmitProof } : {}),
          }),
        ),
      })
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null
        setError(data?.error || "Couldn't send your application. Try again.")
        setStatus("error")
        setHeldHeights(null)
        submittedRef.current = false
        submissionStartedRef.current = false
        return
      }
      // Fence late Lead responses before browser identity is cleared. Promise
      // callbacks cannot interleave within this block, and every later callback
      // also checks both this flag and the generation it captured.
      alreadyFiledRef.current = true
      leadSaveGenerationRef.current += 1
      leadIdRef.current = null
      metaBindingTokenRef.current = null
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      // Clear saved draft on successful submission
      if (typeof window !== "undefined") {
        try {
          localStorage.removeItem(DRAFT_STORAGE_KEY)
        } catch {
          // Silently ignore
        }
        clearMetaLeadSession()
      }
      // The lead ref is no longer read here: the booking now happens BEFORE this
      // submit, so the ref has to arrive with the lead saves instead (see
      // saveDraft). Reading it from this response would be too late to prefill
      // the calendar the applicant has already used. The same holds for the
      // prospectRef this response also carries: the picker already posted the
      // one the lead save handed back.
      //
      // No event fires here. This POST is the form catching up with a booking
      // Cal.com already confirmed; the conversion, booking_confirmed, is fired
      // server-side by the Cal webhook once the stamp has landed, exactly once
      // per booking uid. A client event at this point would be a second count
      // of the same act, and the pre-ruling `apply_submitted` it replaced was
      // exactly that.
      // A submitted application does NOT end inside /apply. When a booking page
      // is configured, hand the applicant off to the DEDICATED /book page — a
      // real full page where they only pick a call time, never an iframe of a
      // page rendered inside a page (owner, 2026-07-18: "a dedicated call
      // booking page... they should no longer be on the application page").
      // /book then routes them on to the post-booking VSL page. Stash name +
      // email in sessionStorage (never the URL — personal data stays out of
      // query strings) so /book can prefill the Cal booking and keep the lead
      // attributed to the same email.
      // The booking already happened, on the last step, so there is nothing left
      // to hand off to /book. Land them on the post-booking page instead: it is
      // part two of the VSL (the pre-call video slot, what to expect, add to
      // calendar), which is the selling this flow would otherwise skip.
      const confirmedBooking = confirmed ?? booking
      if (confirmedBooking && typeof window !== "undefined") {
        const params = new URLSearchParams()
        if (confirmedBooking.uid) params.set("uid", confirmedBooking.uid)
        if (confirmedBooking.start) params.set("start", confirmedBooking.start)
        if (confirmedBooking.end) params.set("end", confirmedBooking.end)
        const query = params.toString()
        const target = `/what-happens-next${query ? `?${query}` : ""}`
        setRedirectTarget(target)
        setStatus("redirecting")
        window.location.assign(target)
        return
      }
      setStatus("done")
    } catch {
      setError("Couldn't send your application. Check your connection and try again.")
      setStatus("error")
      setHeldHeights(null)
      submittedRef.current = false
      submissionStartedRef.current = false
    }
  }

  function captureLeadAnalytics(source: ApplicationAnswers, candidateEventId: unknown) {
    if (leadSent.current) return
    const metaEventId = eligibleMetaEventId(candidateEventId)
    if (!metaEventId) return
    leadSent.current = true
    writeSessionValue(LEAD_META_EVENT_SESSION_KEY, metaEventId)
    if (resumedLead.current) return
    stitchLeadEmail(source.email)
    // lead_captured: valid contact information SAVED, meaning the server has
    // acknowledged the durable row. The Meta Lead pixel rides on the same event
    // through the server-issued dedup id (web/lib/meta-pixel.ts).
    milestones.leadCaptured({
      ...applyEventProps(source.program ?? null, moneyArm),
      meta_event_id: metaEventId,
    })
  }

  function rememberLeadBinding(
    candidateLeadId: unknown,
    candidateBindingToken: unknown,
    candidateEventId: unknown,
  ): boolean {
    const leadId = eligibleLeadId(candidateLeadId)
    const metaBindingToken = eligibleMetaBindingToken(candidateBindingToken)
    const activeDraftId = eligibleDraftId(draftIdRef.current)
    if (
      !leadId ||
      !metaBindingToken ||
      !activeDraftId ||
      readStoredDraftId() !== activeDraftId ||
      resumeToken
    ) {
      return false
    }

    // Before a Meta occurrence exists, an email correction may cause canonical
    // capture to choose a different row. The response carrying the first valid
    // event id wins. Once the event is sent, the binding is immutable locally.
    const canReplace =
      !metaBindingTokenRef.current ||
      (!leadSent.current && eligibleMetaEventId(candidateEventId))
    if (!canReplace) return false
    leadIdRef.current = leadId
    metaBindingTokenRef.current = metaBindingToken
    writeSessionValue(LEAD_ID_SESSION_KEY, leadId)
    writeSessionValue(META_BINDING_SESSION_KEY, metaBindingToken)
    writeHistoryMetaTabClaim(activeDraftId)
    return true
  }

  /** `stepList` is the list these answers resolve to. An answer can change
   * which steps follow (a photo answer, a money answer, "Not yet"), so the step
   * he is moving to is named from the list his new answers produce, never the
   * list this render was drawn with: that is how a row records that it ended
   * on a product, which is what keeps it out of the recovery emails. */
  function saveDraft(
    source: ApplicationAnswers,
    nextStepIndex: number,
    stepList: ApplicationStep[] = steps,
  ): Promise<void> | undefined {
    if (!resumeReady || alreadyFiledRef.current || submissionStartedRef.current) return
    const nextStep = stepList[nextStepIndex] ?? step
    draftPositionRef.current = { id: nextStep.id, index: nextStepIndex }
    const generation = leadSaveGenerationRef.current
    leadSaveQueueRef.current = leadSaveQueueRef.current.then(async () => {
      if (alreadyFiledRef.current || generation !== leadSaveGenerationRef.current) return
      try {
        // Build the body only when this save reaches the front of the queue. A
        // prior response may just have supplied the canonical Lead row id.
        writeDraftNow(source)
        const res = await fetch("/api/coaching/apply", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            withApplyHoneypot({
              stage: "lead",
              answers: { ...source, source: resolveSource(), ...(moneyArm ? { money_arm: moneyArm } : {}) },
              posthog_distinct_id: getPosthogDistinctId(),
              ...resolveEntry(),
              ...(resumeUnhydrated.current ? { unhydrated_resume: true } : {}),
              last_step_id: nextStep.id,
              last_step_index: nextStepIndex,
              lead_id: leadIdRef.current,
              resume_token: resumeToken,
              meta_binding_token: metaBindingTokenRef.current,
              meta_draft_id: draftIdRef.current,
            }),
          ),
        })
        if (alreadyFiledRef.current || generation !== leadSaveGenerationRef.current) return
        // The lead save hands back the booking reference, because the booking
        // happens on the last step BEFORE any submit — it used to arrive with
        // the submit response, which is too late to prefill the calendar.
        const data = (await res.json()) as {
          ok?: boolean
          prospectRef?: string | null
          leadId?: string | null
          moneyArm?: string | null
          metaBindingToken?: string | null
          metaEventId?: string | null
        } | null
        if (alreadyFiledRef.current || generation !== leadSaveGenerationRef.current) return
        if (data?.prospectRef) setProspectRef(data.prospectRef)
        // The stored row is authoritative on the arm. A second device derives
        // its own from a different distinct id, so without adopting this the
        // man is asked the question the other arm shows while his row records
        // the first one - and his real answer then hides behind a question he
        // never saw.
        if (isMoneyArm(data?.moneyArm) && data.moneyArm !== answers.money_arm) {
          const adopted = data.moneyArm
          setAnswers((prev) => ({ ...prev, money_arm: adopted }))
        }
        const persistedLeadId = eligibleLeadId(data?.leadId)
        if (res.ok && data?.ok === true && persistedLeadId) {
          const bindingAccepted = rememberLeadBinding(
            persistedLeadId,
            data.metaBindingToken,
            data.metaEventId,
          )
          if (resumeToken || bindingAccepted) {
            captureLeadAnalytics(source, data.metaEventId)
          }
        }
      } catch {
        // Attribution falls back to the attendee email. Not worth a failure.
      }
    })
    return leadSaveQueueRef.current
  }

  /**
   * Autosave the partial application to the SERVER, debounced.
   *
   * Every keystroke already calls persistAnswers(), but that is localStorage:
   * it survives a reload in HIS browser and tells us nothing. The only server
   * write was saveDraft() inside advanceFrom(), so an answer reached us only
   * when a step was completed.
   *
   * That is the exact failure the owner reported on 2026-07-11: "rahul clearly
   * filled out way more of the application and our saved progress only includes
   * part of his answers. he actually answered ALL the questions and just had
   * some error preventing him from submitting." His answers existed the whole
   * time, in a browser we cannot read.
   *
   * Gated on a valid name + email because that pair IS the lead: before it,
   * there is no row to update and the write would be junk. After it, every
   * further answer is worth keeping even if he never finishes, which is the
   * whole point of capturing a lead early.
   */
  // Seeded with a restored draft's answers, so an untouched restore is not a
  // change and posts nothing: the first keystroke or tap changes the
  // fingerprint and saves at the step on screen. A resume link starts empty and
  // keeps its save right after hydration, which mints the booking reference.
  const lastSavedRef = useRef<string>(restored ? JSON.stringify(restored.answers) : "")
  // The timer below fires up to 1.5s after the render that set it. The save
  // runs from the latest render, so everything saveDraft reads besides the
  // answers and step the timer hands it (the entry and resume props, the
  // booking, the steps) is current when the request body is built, and none of
  // it has to be listed as a dependency of the debounce.
  const saveAutosavedDraft = useEffectEvent((source: ApplicationAnswers, index: number) => {
    saveDraft(source, index)
  })
  useEffect(() => {
    if (status !== "idle" || !resumeReady) return
    const name = (answers.name ?? "").trim()
    const email = (answers.email ?? "").trim()
    if (!name || !EMAIL_RE.test(email)) return

    const fingerprint = JSON.stringify(answers)
    if (fingerprint === lastSavedRef.current) return

    // 1.5s after he stops typing. Long enough that a sentence is one write,
    // short enough that walking away mid-answer still lands it.
    const t = setTimeout(() => {
      lastSavedRef.current = fingerprint
      saveAutosavedDraft(answers, safeIndex)
    }, 1500)
    return () => clearTimeout(t)
  }, [answers, status, safeIndex, resumeReady])

  /**
   * A visit restored straight onto the calendar has sent nothing yet, so it
   * has no booking reference, and /api/apply/book refuses a booking without
   * one ("Start your application first, then pick a time"). Walking the form
   * mints it on the step-1 save, so on a normal walk this never fires. One
   * save at a time, with the step he is already on (the booking step), so the
   * stored position is unchanged. A save that comes back without a reference
   * (a dropped connection) is tried again after a pause, up to three times
   * per mount, because without it he cannot book until he reloads.
   */
  const bookingRefSaveRef = useRef(false)
  const bookingRefAttemptsRef = useRef(0)
  const [bookingRefRetry, setBookingRefRetry] = useState(0)
  useEffect(() => {
    if (!onBookingStep || prospectRef || !resumeReady || status !== "idle") return
    if (bookingRefSaveRef.current) return
    const name = (answers.name ?? "").trim()
    const email = (answers.email ?? "").trim()
    if (!name || !EMAIL_RE.test(email)) return
    const save = saveDraft(answers, safeIndex)
    if (!save) return
    bookingRefSaveRef.current = true
    bookingRefAttemptsRef.current += 1
    void save.then(() => {
      if (bookingRefAttemptsRef.current >= 3) return
      // When the reference arrived, the re-run below sees prospectRef and stops.
      setTimeout(() => {
        bookingRefSaveRef.current = false
        setBookingRefRetry((n) => n + 1)
      }, 2000)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBookingStep, prospectRef, resumeReady, status, answers, safeIndex, bookingRefRetry])

  /**
   * Record the shown default for any control on this step the applicant left
   * alone. Only the age sliders have one, and they show a real range from the
   * moment they render — so leaving them untouched means "that range is fine",
   * not "no answer". Done here, in an event handler, rather than in an effect
   * that would seed state on mount (a cascading render the lint rejects).
   */
  function withStepDefaults(source: ApplicationAnswers): ApplicationAnswers {
    let out = source
    for (const q of step.questions) {
      if (!q.defaultValue) continue
      if ((out[q.id] ?? "").trim()) continue
      if (out === source) out = { ...source }
      out[q.id] = q.defaultValue
    }
    if (out !== source) {
      persistAnswers(out)
      setAnswers(out)
    }
    return out
  }

  function advanceFrom(input: ApplicationAnswers) {
    if (!resumeReady) return
    const source = withStepDefaults(input)
    const failure = getStepValidation(source)
    if (failure) {
      setError(failure.message)
      return
    }
    // Step 1 (name + email): capture the reachable lead the instant the email is
    // a valid format. Never gate the save on a network reachability check — a
    // false "unreachable" used to drop a real prospect entirely (owner,
    // 2026-07-11, after the funnel diagnosis showed the 92% step-1 leak).
    if (step.id === CONTACT_STEP_ID) {
      stitchLeadEmail(source.email)
    }

    // The steps his answers produce NOW, which can differ from the list this
    // render was drawn with: the answer just given may add or remove what
    // follows it.
    const nextSteps = applicationStepsForAnswers(source)
    const lastNow = safeIndex >= nextSteps.length - 1
    const nextStepIndex = lastNow ? safeIndex : safeIndex + 1
    saveDraft(source, nextStepIndex, nextSteps)

    if (lastNow) {
      void submit(source)
      return
    }
    // application_started: proceeding beyond contact capture. The emitter holds
    // it until lead_captured has gone out, so the two never land out of order.
    if (step.id === CONTACT_STEP_ID) {
      const nextStep = nextSteps[nextStepIndex] ?? step
      milestones.contactStepAdvanced({
        ...applyEventProps(programTag ?? null, moneyArm),
        step_id: nextStep.id,
        step_index: nextStepIndex,
        step_number: nextStepIndex + 1,
        branch,
      })
    }
    setStepIndex(() => nextStepIndex)
    setError(null)
  }

  // A confirmed booking IS the submission (owner, 2026-07-25). The button
  // becomes a confirmation rather than the thing that files the application, so
  // a man who books and then closes the tab is still an applicant with a call on
  // the calendar — which is the outcome we actually wanted from him.
  function handleBooked(confirmed: ConfirmedBooking) {
    // Read before React commits: the picker's own "done" state and this
    // booking land in one batched render, so the DOM still shows the open
    // picker here, and its height is the space to keep.
    setHeldHeights({
      picker: pickerBoxRef.current?.offsetHeight ?? 0,
      root: rootRef.current?.offsetHeight ?? 0,
    })
    setBooking(confirmed)
    setError(null)
    if (submittedRef.current) return
    submittedRef.current = true
    void submit(answers, confirmed)
  }

  function next() {
    const failure = getStepValidation(answers)
    if (failure) {
      track(AnalyticsEvent.ApplyContinueClicked, { ...stepEventProps(), blocked: true })
      trackValidationError(failure)
      setError(failure.message)
      return
    }
    track(AnalyticsEvent.ApplyContinueClicked, { ...stepEventProps(), blocked: false })
    // On the booking step the application was already filed the moment the
    // booking confirmed, so the button only carries them onward.
    if (step.id === BOOKING_STEP_ID) {
      void submit(answers, booking ?? undefined)
      return
    }
    void advanceFrom(answers)
  }

  /** "I'd still like a call" on a product step: carry on toward the call. The
   * save it makes names the next step, so the row no longer reads as ending on
   * the product. */
  function continueFromProduct() {
    track(AnalyticsEvent.ApplyContinueClicked, { ...stepEventProps(), blocked: false })
    void advanceFrom(answers)
  }

  function back() {
    setError(null)
    if (status === "error") setStatus("idle")
    setStepIndex(() => Math.max(safeIndex - 1, 0))
  }

  /**
   * Single-select: record the choice and auto-advance one-question steps.
   * The advance is deferred one tick so the tapped option's "selected" highlight
   * paints first — advancing synchronously batches the answer update and the step
   * change into one render, so the tap never visibly registers before the screen
   * jumps to the next question (read as a dead click; fixed 2026-07-14).
   */
  function choose(q: ApplicationQuestion, value: string) {
    markStarted("selection")
    const updated = { ...answers, [q.id]: value }
    persistAnswers(updated)
    setAnswers(updated)
    setError(null)
    if (step.questions.length === 1) {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current)
      advanceTimerRef.current = setTimeout(() => {
        advanceTimerRef.current = null
        void advanceFrom(updated)
      }, 180)
    }
  }

  /** Enter on a non-terminal field advances focus to the next field in the step
   * instead of submitting — so pressing the mobile keyboard's Next after the
   * name jumps to email rather than firing a validation error (owner,
   * 2026-07-11, the #1 step-1 friction). */
  function focusField(qid: string) {
    const el = document.getElementById(`q-${qid}`)
    if (!el) return
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.focus()
      return
    }
    const target =
      el.querySelector<HTMLElement>("input, textarea") ?? el.querySelector<HTMLElement>("button")
    target?.focus()
  }

  if (status === "redirecting") {
    // The browser is mid-navigation to /what-happens-next; this covers the
    // beat before the next page paints, so the dead form is not left on
    // screen. A bare spinner on purpose (owner, on being shown the old
    // wording: get rid of the loading screen). The old version carried a
    // heading and two sentences written for a flow where booking came AFTER
    // submit, so it told a man who had just booked "You're not done yet.
    // Book your call" while opening a page that is not the booking page. No
    // words, nothing to go stale. The spinner is a link to the exact URL
    // being opened, so a stalled navigation is still escapable by hand.
    // The wrapper holds the height the form had when the booking confirmed, so
    // swapping the form for this spinner leaves the footer where it was until
    // the next page paints, instead of pulling it up into view (0.05 layout
    // shift on a 390px phone).
    return (
      <div style={heldHeights ? { minHeight: heldHeights.root } : undefined}>
        <div className="w-full rounded-[var(--radius-xl)] border border-[var(--color-rule)] bg-[var(--color-paper-high)] p-8 text-center sm:p-12">
          <a
            href={redirectTarget}
            aria-label="Continue to the next step"
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-paper-high)] text-[var(--color-vermilion)]"
          >
            <Loader2 size={26} className="animate-spin" aria-hidden />
          </a>
        </div>
      </div>
    )
  }

  if (status === "done") {
    return (
      <div className="w-full rounded-[var(--radius-xl)] border border-[var(--color-rule)] bg-[var(--color-paper-high)] p-8 text-center sm:p-12">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-vermilion)] text-[var(--color-paper)]">
          <Check size={26} aria-hidden />
        </span>
        <h2 className="serious-display mt-6 text-[clamp(1.8rem,3.4vw,2.6rem)] leading-[1.05] text-[var(--color-ink)]">
          Thank you. We&apos;re glad you reached out.
        </h2>
        <p className="mx-auto mt-4 max-w-[32rem] text-[1.02rem] leading-[1.6] text-[var(--color-ink-muted)]">
          We&apos;ll go through it and email you back with the next step, usually
          within a couple of days. Keep an eye on your inbox for a confirmation.
        </p>
      </div>
    )
  }

  return (
    // The rail stays pinned under the masthead and the form takes the remaining
    // height and CENTRES in it, so a short first step reads as deliberate rather
    // than as a form that fell to the top of a tall page. Measured before this:
    // 348px of dead space under the applied-this-week line on step one.
    //
    // The height is spelled out rather than inherited, and it has to be. The
    // marketing layout stretches `main` with `flex-1`, which gives it a USED
    // height but leaves its specified height `auto`, so a percentage height on
    // any descendant resolves against `auto` and collapses to the content. That
    // is why `h-full` on the section measured 544px inside an 832px main. The
    // alternative was making the shared `main` a flex column, which changes
    // margin behaviour on every marketing page to fix one.
    //
    // The two numbers are the FUNNEL chrome, measured across breakpoints: a
    // 65px masthead throughout, and a footer that is 119px until its links stop
    // wrapping at Tailwind's sm (exactly 640px) and 84px above it. Re-measure
    // both if the funnel masthead or footer ever changes.
    <div
      ref={rootRef}
      className="flex min-h-[calc(100svh-184px)] flex-col sm:min-h-[calc(100svh-149px)]"
    >
      <div ref={honeypotContainerRef}>
        <HoneypotField />
      </div>
      {/* THE RAIL SITS AT THE TOP OF THE PAGE, FULL WIDTH, not inside a card
          (owner pick, 2026-08-22, from design/2026-08-22-apply-revised). It
          reads as part of the page chrome directly under the masthead rather
          than as a decoration on a widget, which is the whole point of the
          option he chose: the application IS the page, not a card sitting on
          one. Full-bleed out of the reading column by design. */}
      <div className="h-[3px] w-full bg-[var(--color-rule)]">
        <div
          className="h-full bg-[var(--color-vermilion)] transition-[width] duration-300 ease-out"
          style={{ width: `${Math.max(progress, 4)}%` }}
        />
      </div>

      <div className="flex flex-1 flex-col justify-center py-10">
      {/* scroll-mt-24 pairs with the step-change scrollIntoView above so the
          eyebrow, and the question under it, land below the sticky masthead
          whenever that scroll is needed, never under it. */}
      <div ref={stepColumnRef} className="mx-auto w-full max-w-[34rem] scroll-mt-24 px-5 pb-4 sm:px-8">
        {/* The eyebrow is crimson here, not grey. It is the only colour above
            the question, and it is what tells a man which part of the form he
            is in now that there is no card edge to frame it. A step whose
            approved wording carries no eyebrow shows none. */}
        {step.hideEyebrow ? null : (
          <div className="mb-5 text-[0.74rem] font-semibold uppercase tracking-[0.14em] text-[var(--color-vermilion)]">
            {step.eyebrow}
          </div>
        )}

      {/* Questions. Every question label uses ONE identical font (owner,
          2026-07-17: "make sure your fonts are consistent throughout the
          entire application") — no serif display heading on the first
          question, no smaller style on the rest. Only spacing differs. */}
      {/* The focus target on a step change; the scroll targets the column
          above. No min-height: the old min-h-[15rem] left a permanent blank
          band inside the card on the short first step, which read as a broken
          empty form. */}
      <div ref={questionsRef} tabIndex={-1} className="outline-none">
        {step.id === BOOKING_STEP_ID ? (
          <div>
            {/* One short heading, then straight into the days (owner,
                2026-07-31: "you have book your call and then the red line and
                then you are not done yet and then a lot of text and only then
                do you see the dates... just show them the three days"). The
                not-done-until-you-book message survives in one line; the
                second paragraph and the Cal.com iframe's own card/whitespace
                are gone, replaced by ApplyBookingPicker - a plain, borderless
                day-chip + time-slot list built directly into the page. */}
            <p className="text-[1.05rem] font-semibold leading-snug text-[var(--color-ink)]">
              You&apos;re not done until you book a time.
            </p>
            {/* The picker's space is reserved before its open times arrive.
                It mounts as one "Loading open times…" line and then becomes the
                day chips plus the time grid, and because this column is
                vertically centred, that growth moved the heading up and the
                trust badges down after the fetch landed, with no tap to excuse
                it: 0.14 layout shift on a 390px phone. The reservation is
                the ready height whenever a day has a full grid of times, which
                is every day but a late-evening today: the day row (min-h-11,
                2.75rem), the time grid's mt-3 (0.75rem) and its
                max-h-[12.5rem] scroll cap, plus the required phone field under
                them (mt-4, its "Phone number" label with no helper line, and
                the 3rem input). Measured at 390px the ready picker is
                350.08px for every day, so the reservation is 21.88rem
                (350.08px).
                Once booked, `heldHeights` keeps the height the open
                picker had, so the one-line "Booked:" confirmation that
                replaces it collapses nothing, and the 21.88rem reservation goes:
                if the submit after the booking fails, `heldHeights` is
                released and the box hugs the "Booked:" line. */}
            <div
              ref={pickerBoxRef}
              className={cn("mt-3", !booking && "min-h-[21.88rem]")}
              style={heldHeights ? { minHeight: heldHeights.picker } : undefined}
            >
              <ApplyBookingPicker
                name={(answers.name ?? "").trim()}
                email={(answers.email ?? "").trim()}
                program={answers.program ?? null}
                prospectRef={prospectRef}
                awaitPendingSave={() => leadSaveQueueRef.current}
                onSlotsLoadFailure={({ reason, status, elapsedMs }) => {
                  track(AnalyticsEvent.ApplySlotsLoadFailed, {
                    ...stepEventProps(),
                    reason,
                    ...(status !== undefined ? { http_status: status } : {}),
                    elapsed_ms: elapsedMs,
                  })
                }}
                onBooked={handleBooked}
              />
            </div>
            {/* Escape hatch: a broken slots fetch or booking call must not trap
                someone on a dead step. This sends them to the real booking page,
                where the same booking still reaches us through the Cal webhook —
                and because a booking counts as the submission, they become a full
                application even if they never come back to this tab. */}
            <p className="mt-4 text-[0.85rem] leading-[1.5] text-[var(--color-ink-soft)]">
              Calendar not loading?{" "}
              <a
                href={bookingUrl ?? "/book"}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[var(--color-ink)] underline underline-offset-4"
              >
                Book your call here instead
              </a>
              .
            </p>
          </div>
        ) : null}
        {productCard ? (
          // A PRODUCT STEP, his approved wording only. The heading, the one
          // product card (its own checkout copy, live price and real page) and
          // either the small "I'd still like a call" link that carries on to
          // the call, or, after "Not yet", the line and the note that end the
          // application on the product with no calendar and no booking button.
          <div>
            <h2 className="text-[1.7rem] font-semibold leading-[1.2] tracking-[-0.02em] text-[var(--color-ink)] sm:text-[2rem]">
              Here&apos;s a good place to start
            </h2>
            {step.id === "not_ready" ? (
              <p className="mt-1.5 text-[0.95rem] leading-[1.55] text-[var(--color-ink-muted)]">
                That&apos;s fine. The application call is for men who are ready to start a program. Until then, this is the best next step for where you are now.
              </p>
            ) : null}
            <div className="mt-5">
              <ProductCardLink card={productCard} />
            </div>
            {step.id === "not_ready" ? (
              <p className="mt-5 text-[0.95rem] leading-[1.55] text-[var(--color-ink-muted)]">
                When you&apos;re ready for a program, come back to this page and pick a time for your application call.
              </p>
            ) : null}
            {PRODUCT_STEP_IDS_WITH_CONTINUE.has(step.id) ? (
              <button
                type="button"
                onClick={continueFromProduct}
                className="mt-5 text-[0.92rem] font-semibold text-[var(--color-ink)] underline underline-offset-4"
              >
                I&apos;d still like a call
              </button>
            ) : null}
          </div>
        ) : null}
        {step.questions.map((q, index) => (
          <div key={q.id} className={index === 0 ? undefined : "mt-8"}>
            <label
              htmlFor={`q-${q.id}`}
              className={cn(
                "block font-semibold text-[var(--color-ink)]",
                // The approved mock puts ONE question on screen at display
                // size. A step that groups fields (name + email, city + age)
                // keeps the field-label size, because "Name" rendered at
                // 2rem is a label pretending to be a question. Same family
                // either way, which is the consistency he asked for; only the
                // scale moves.
                step.questions.length === 1
                  ? "text-[1.7rem] leading-[1.2] tracking-[-0.02em] sm:text-[2rem]"
                  : "text-[1.05rem] leading-snug",
              )}
            >
              {q.label}
              {q.required ? (
                <span className="text-[var(--color-vermilion)]">{"\u00A0*"}</span>
              ) : q.labelSaysOptional ? null : (
                <span className="ml-2 align-middle text-[0.72rem] font-normal uppercase tracking-[0.1em] text-[var(--color-ink-soft)]">
                  Optional
                </span>
              )}
            </label>
            {q.help ? (
              <p id={`q-${q.id}-help`} className="mt-1.5 text-[0.95rem] leading-[1.55] text-[var(--color-ink-muted)]">
                {q.help}
              </p>
            ) : null}
            <div className="mt-2.5">
              <QuestionInput
                q={q}
                value={answers[q.id] ?? ""}
                errorId={stepFailure?.field_id === q.id ? fieldErrorId : null}
                onChange={(value) => setAnswer(q.id, value)}
                onChoose={(value) => choose(q, value)}
                onEnter={
                  index < step.questions.length - 1
                    ? () => focusField(step.questions[index + 1].id)
                    : next
                }
              />
            </div>
          </div>
        ))}
      </div>

      {error ? (
        <p id={fieldErrorId ?? undefined} role="alert" className="mt-6 text-[0.92rem] font-medium text-[var(--color-vermilion)]">
          {error}
        </p>
      ) : null}

      {/* Why Continue is disabled — a disabled button can't fire onClick to
          surface the inline error, so explain it in place instead of leaving the
          greyed button unexplained. Muted/instructional, not an alarm. The
          generic "fill in the fields above" filler was cut (owner,
          2026-07-17: "no filler helper lines under the fields") — an
          invalid-email message still shows (it's the one useful case), and a
          single-question step still gets its one-line nudge. */}
      {/* Only the invalid-email message survives (the one useful case). The
          "Answer the question above to keep going." nudge was cut on every step
          (owner, 2026-07-18: "get rid of this on all pages"). */}
      {/* The booking step is deliberately excluded: its message ("Pick a time
          above…") kept showing after a time WAS picked, when the real blocker
          was the phone field, so it read as stale and wrong. The picker owns
          its own in-place explanations now, and the step's heading already
          says a booking is required. */}
      {!error &&
      continueDisabled &&
      status !== "submitting" &&
      stepFailure?.error_type === "invalid_email" ? (
        <p id={fieldErrorId ?? undefined} role="alert" className="mt-6 text-[0.9rem] text-[var(--color-ink-soft)]">
          {stepFailure?.message}
        </p>
      ) : null}

      {/* Controls */}
      <div className="mt-8 flex items-center justify-between gap-4 border-t border-[var(--color-rule)] pt-6">
        {safeIndex === 0 ? (
          // Nothing sits beside Continue on step 1. The old line sold the form
          // ("All multiple choice and takes under two minutes") and the owner
          // cut it: we rely on a man wanting coaching, not on the form being
          // short. Selling the effort implies the thing on the other side is
          // not worth much effort, which is the opposite of the frame.
          <span />
        ) : (
          <button
            type="button"
            onClick={back}
            disabled={status === "submitting"}
            className={cn(
              "inline-flex items-center gap-1.5 py-3 text-[0.92rem] font-semibold text-[var(--color-ink-muted)] transition-colors hover:text-[var(--color-ink)] disabled:cursor-not-allowed disabled:opacity-[0.38]",
            )}
          >
            <ArrowLeft size={15} aria-hidden />
            <span>Back</span>
          </button>
        )}
        {/* ONE confirmation on the booking step (owner: "we just need the one
            confirmation"). The picker already carries the real action, a
            "Confirm <day> at <time>" button, and a confirmed booking IS the
            submission, so this footer button sitting beside it was a second
            button that looked like the same job. Until a booking exists it
            renders nothing; afterwards it stays as the way onward for anyone
            whose redirect did not fire. */}
        {/* A product step carries its own way onward ("I'd still like a
            call"), or none after "Not yet", so no Continue sits beside it. */}
        {productCard || (step.id === BOOKING_STEP_ID && !booking && status !== "submitting") ? (
          <span />
        ) : (
        <button
          type="button"
          onClick={next}
          disabled={continueDisabled}
          aria-disabled={hydrated && stepBlocked}
          className={marketingActionClass({ variant: "accent", size: "lg" })}
        >
          {status === "submitting" ? (
            <>
              <Loader2 size={16} className="animate-spin" aria-hidden />
              <span>Sending…</span>
            </>
          ) : step.id === BOOKING_STEP_ID ? (
            <>
              <span>{booking ? "Finish" : "Book a time to finish"}</span>
              <ArrowRight size={16} aria-hidden />
            </>
          ) : isLast ? (
            <>
              <span>Submit application</span>
              <ArrowRight size={16} aria-hidden />
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight size={16} aria-hidden />
            </>
          )}
        </button>
        )}
      </div>
      </div>
        {children}
      </div>
    </div>
  )
}
